import * as React from "react";
import { Box, Button, Card, Chip, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/ssr/DownloadSimple";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/ssr/UploadSimple";
import { toast } from "sonner";

import type { CampaignRequestDocument, DocumentKind } from "@/hooks/api/use-campaign-requests";
import {
	useDeleteCampaignRequestDocument,
	useDownloadCampaignRequestDocument,
	useUploadCampaignRequestDocument,
} from "@/hooks/api/use-campaign-requests";

import {
	DOCUMENT_KIND_ACCEPT,
	DOCUMENT_KIND_COLORS,
	DOCUMENT_KIND_HELP,
	DOCUMENT_KIND_LABELS,
	DOCUMENT_KIND_OPTIONS,
	formatBytes,
	formatDate,
	MAX_DOCUMENT_BYTES,
} from "../constants";
import { labelOf } from "../humanise";
import { HelpTooltip } from "./help-tooltip";

/** "Lead list" → "Upload lead list". A button says what it does with the thing it does it to. */
function uploadLabelFor(kind: DocumentKind): string {
	const label = DOCUMENT_KIND_LABELS[kind];
	if (!label) return "Upload";
	return `Upload ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

export interface DocumentUploadButtonProps {
	requestId: string;
	kind: DocumentKind;
	/** Overrides the label built from the kind. Used where the row beside it already names the thing. */
	label?: string;
	disabled?: boolean;
	size?: "small" | "medium";
}

/**
 * One kind, one button.
 *
 * The kind is a path segment on the upload endpoint and it decides which content types the API
 * accepts, so it has to be known before the file is chosen. Knowing it is not the same as asking
 * for it: where a screen takes exactly one kind, the answer is already in, and a select offering
 * that single answer is a control with nothing to choose.
 */
export function DocumentUploadButton({
	requestId,
	kind,
	label,
	disabled = false,
	size = "small",
}: DocumentUploadButtonProps): React.JSX.Element {
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const uploadMutation = useUploadCampaignRequestDocument();

	const handleFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		// Cleared straight away so re-picking the same file fires a change event again.
		event.target.value = "";

		if (!file) return;

		if (file.size > MAX_DOCUMENT_BYTES) {
			toast.error(`${file.name} is over the ${formatBytes(MAX_DOCUMENT_BYTES)} limit`);
			return;
		}

		uploadMutation.mutate({ requestId, kind, file });
	};

	return (
		<>
			<Button
				variant="contained"
				size={size}
				startIcon={<UploadSimpleIcon />}
				disabled={disabled || uploadMutation.isPending}
				onClick={() => inputRef.current?.click()}
				sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
			>
				{uploadMutation.isPending ? "Uploading…" : (label ?? uploadLabelFor(kind))}
			</Button>

			<input
				ref={inputRef}
				type="file"
				hidden
				accept={DOCUMENT_KIND_ACCEPT[kind]}
				onChange={handleFile}
				data-testid="campaign-request-document-input"
			/>
		</>
	);
}

export interface DocumentRowsProps {
	requestId: string;
	documents: CampaignRequestDocument[];
	/** A frozen request still shows its paperwork; it just cannot take any more. */
	readOnly?: boolean;
	allowDelete?: boolean;
	/** Off where the surrounding heading already names the kind — the tag would only repeat it. */
	showKind?: boolean;
}

/**
 * The files themselves, with the two things anyone wants to do to them.
 *
 * Download goes through the API rather than a link: the bytes are encrypted at rest, the endpoint
 * decrypts and verifies before it sends anything, and it writes an access-log row naming whoever
 * asked. A plain `<a href>` would carry no Authorization header and be refused.
 */
export function DocumentRows({
	requestId,
	documents,
	readOnly = false,
	allowDelete = true,
	showKind = true,
}: DocumentRowsProps): React.JSX.Element {
	const downloadMutation = useDownloadCampaignRequestDocument();
	const deleteMutation = useDeleteCampaignRequestDocument();

	return (
		<Stack spacing={1}>
			{documents.map((document) => (
				<Box
					key={document.id}
					sx={{
						alignItems: "center",
						border: "1px solid var(--mui-palette-divider)",
						borderRadius: 1,
						display: "flex",
						gap: 2,
						justifyContent: "space-between",
						p: 1.5,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
							{document.originalFilename}
						</Typography>
						{/* The MIME type used to sit here too, beside a filename that ends in .csv. */}
						<Typography variant="caption" color="text.secondary">
							{formatBytes(document.sizeBytes)} · {formatDate(document.createdAt)}
						</Typography>
					</Box>

					<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
						{showKind ? (
							<Chip
								color={DOCUMENT_KIND_COLORS[document.kind] ?? "default"}
								label={labelOf(DOCUMENT_KIND_LABELS, document.kind)}
								size="small"
								variant={DOCUMENT_KIND_COLORS[document.kind] === "default" ? "outlined" : "soft"}
							/>
						) : null}

						<HelpTooltip title="Download" describeChild={false}>
							<span>
								<IconButton
									aria-label="Download"
									size="small"
									disabled={downloadMutation.isPending}
									onClick={() => {
										downloadMutation.mutate({ requestId, document });
									}}
								>
									<DownloadSimpleIcon />
								</IconButton>
							</span>
						</HelpTooltip>

						{readOnly || !allowDelete ? null : (
							<HelpTooltip title="Remove" describeChild={false}>
								<span>
									<IconButton
										aria-label="Remove"
										size="small"
										color="error"
										disabled={deleteMutation.isPending}
										onClick={() => {
											deleteMutation.mutate({ requestId, documentId: document.id });
										}}
									>
										<TrashIcon />
									</IconButton>
								</span>
							</HelpTooltip>
						)}
					</Stack>
				</Box>
			))}
		</Stack>
	);
}

export interface DocumentListProps {
	requestId: string;
	documents: CampaignRequestDocument[];
	/** The kinds this screen is asking for. One kind means one button and no picker. */
	kinds?: DocumentKind[];
	/** Show only documents of these kinds. Defaults to everything attached. */
	filterKinds?: DocumentKind[];
	title?: string;
	emptyText?: string;
	readOnly?: boolean;
	allowDelete?: boolean;
}

/** What is attached to a request, and the button that adds to it. */
export function DocumentList({
	requestId,
	documents,
	kinds,
	filterKinds,
	title = "Documents",
	emptyText = "Nothing attached yet.",
	readOnly = false,
	allowDelete = true,
}: DocumentListProps): React.JSX.Element {
	const offered = React.useMemo(
		() => (kinds ? DOCUMENT_KIND_OPTIONS.filter((option) => kinds.includes(option.value)) : DOCUMENT_KIND_OPTIONS),
		[kinds]
	);

	const [kind, setKind] = React.useState<DocumentKind>(offered[0]?.value ?? "other");

	// A picker earns its place only on a screen that takes several kinds — the documents tab of a
	// request, and the admin's lists tab. Everywhere else the screen has already decided.
	const picking = offered.length > 1;

	const shown = React.useMemo(
		() => (filterKinds ? documents.filter((document) => filterKinds.includes(document.kind)) : documents),
		[documents, filterKinds]
	);

	return (
		<Card variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1}
					sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
				>
					<Typography variant="h6">{title}</Typography>

					{readOnly ? null : (
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							{picking ? (
								<TextField
									select
									size="small"
									label="Kind"
									value={kind}
									onChange={(event) => {
										setKind(event.target.value as DocumentKind);
									}}
									sx={{ minWidth: 200 }}
								>
									{offered.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
							) : null}

							<DocumentUploadButton requestId={requestId} kind={kind} label={picking ? "Upload" : undefined} />
						</Stack>
					)}
				</Stack>

				{/* What the chosen kind is for, and only where a choice was made. On a single-kind
				    screen the heading above and the empty line below already say it. */}
				{picking && !readOnly ? (
					<Typography variant="caption" color="text.secondary">
						{DOCUMENT_KIND_HELP[kind]}
					</Typography>
				) : null}

				{shown.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						{emptyText}
					</Typography>
				) : (
					<DocumentRows
						requestId={requestId}
						documents={shown}
						readOnly={readOnly}
						allowDelete={allowDelete}
						showKind={filterKinds?.length !== 1}
					/>
				)}
			</Stack>
		</Card>
	);
}
